import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuditLogs, getAuditLogStats, getAuditLogModules, clearAuditLogs } from './_request';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Box
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const parseLogDetails = (details) => {
  if (!details) return null;
  if (typeof details === 'object') return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return details;
  }
};

const getHumanActionLabel = (action) => {
  if (!action) return 'Activity';
  let clean = action.toUpperCase();
  
  if (clean.includes('LOGIN')) return 'User Login';
  if (clean.includes('LOGOUT')) return 'User Logout';
  if (clean.includes('CLEAR')) return 'Clear Logs';
  if (clean.includes('CREATE') || clean.includes('ADD') || clean.startsWith('POST_')) return 'Create Entry';
  if (clean.includes('UPDATE') || clean.includes('EDIT') || clean.startsWith('PUT_')) return 'Update Entry';
  if (clean.includes('DELETE') || clean.includes('REMOVE')) return 'Delete Entry';
  
  return action.replace(/_/g, ' ');
};

const getHumanReadableSummary = (log) => {
  if (!log) return '';
  const parsedDetails = parseLogDetails(log.details);
  const action = (log.action || '').toUpperCase();
  const module = log.module || 'System';
  const user = log.user_email || 'System user';
  const status = log.status || 'SUCCESS';

  if (status === 'FAILED') {
    return `Failed activity by ${user} in ${module} module.`;
  }

  let itemName = '';
  if (parsedDetails && typeof parsedDetails === 'object') {
    itemName = parsedDetails.title || 
               parsedDetails.title_ur || 
               parsedDetails.name || 
               parsedDetails.name_ur || 
               parsedDetails.book_name || 
               parsedDetails.surah_name ||
               parsedDetails.ayat_title ||
               (parsedDetails.id ? `Record #${parsedDetails.id}` : '');
  }

  const itemText = itemName ? `'${itemName}'` : '';

  if (action.includes('LOGIN')) {
    return `${user} logged into the admin portal.`;
  }
  if (action.includes('CLEAR')) {
    return `${user} cleared the system audit logs.`;
  }
  if (action.includes('CREATE') || action.includes('POST') || action.includes('ADD')) {
    return itemText 
      ? `Added ${itemText} in ${module}`
      : `Created a new entry in ${module}`;
  }
  if (action.includes('UPDATE') || action.includes('PUT') || action.includes('EDIT')) {
    return itemText 
      ? `Updated ${itemText} in ${module}`
      : `Updated details in ${module}`;
  }
  if (action.includes('DELETE') || action.includes('REMOVE')) {
    return itemText 
      ? `Deleted ${itemText} from ${module}`
      : `Deleted an entry from ${module}`;
  }

  return `Performed ${getHumanActionLabel(log.action)} in ${module}`;
};

const AuditLogs = () => {
  const { authToken, userData, themeMode } = useAuth();
  const isDark = themeMode === 'dark';
  
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    totalLogs: 0,
    actionsToday: 0,
    activeUsersToday: 0,
    topModule: 'N/A'
  });
  const [modulesList, setModulesList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const fetchMetadata = useCallback(async () => {
    try {
      const statsRes = await getAuditLogStats(authToken);
      if (statsRes.Result && statsRes.stats) {
        setStats(statsRes.stats);
      }
      const modulesRes = await getAuditLogModules(authToken);
      if (modulesRes.Result && Array.isArray(modulesRes.data)) {
        setModulesList(modulesRes.data);
      }
    } catch (err) {
      console.error("Error fetching metadata:", err);
    }
  }, [authToken]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search.trim(),
        module: selectedModule,
        user: userFilter.trim(),
        action: actionFilter.trim(),
        startDate,
        endDate
      };
      const res = await getAuditLogs(authToken, params);
      if (res.Result) {
        setLogs(res.data || []);
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages || 1);
          setTotalLogs(res.pagination.totalLogs || 0);
        }
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  }, [authToken, page, limit, search, selectedModule, userFilter, actionFilter, startDate, endDate]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleResetFilters = () => {
    setSearch('');
    setSelectedModule('');
    setUserFilter('');
    setActionFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleOpenDetails = (log) => {
    setSelectedLog(log);
    setActiveTab(0);
    setCopied(false);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedLog(null);
  };

  const handleCopyPayload = () => {
    if (!selectedLog?.details) return;
    const parsed = parseLogDetails(selectedLog.details);
    const textToCopy = typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed);
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClearLogs = async () => {
    if (window.confirm("Are you sure you want to clear audit logs? This action cannot be undone.")) {
      try {
        const res = await clearAuditLogs(authToken);
        if (res.Result) {
          alert("Audit logs cleared successfully.");
          fetchMetadata();
          fetchLogs();
        } else {
          alert(res.Msg || "Failed to clear audit logs.");
        }
      } catch (err) {
        alert("Error clearing audit logs.");
      }
    }
  };

  const handleExportCSV = () => {
    if (!logs.length) {
      alert("No logs available to export.");
      return;
    }
    const headers = ["ID", "Timestamp", "User Email", "Role", "Action", "Module", "Activity Summary", "IP Address", "Status", "Raw Details"];
    const csvRows = [headers.join(",")];

    logs.forEach(log => {
      const summaryText = getHumanReadableSummary(log);
      const row = [
        log.id,
        `"${new Date(log.created_at).toLocaleString()}"`,
        `"${log.user_email || ''}"`,
        `"${log.user_role || ''}"`,
        `"${log.action || ''}"`,
        `"${log.module || ''}"`,
        `"${summaryText.replace(/"/g, '""')}"`,
        `"${log.ip_address || ''}"`,
        `"${log.status || ''}"`,
        `"${(log.details || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Audit_Logs_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getActionColor = (action) => {
    if (!action) return 'default';
    const act = action.toUpperCase();
    if (act.includes('CREATE') || act.includes('ADD') || act.startsWith('POST_')) return 'success';
    if (act.includes('UPDATE') || act.includes('EDIT') || act.startsWith('PUT_')) return 'info';
    if (act.includes('DELETE') || act.includes('FAILED')) return 'error';
    if (act.includes('LOGIN')) return 'primary';
    return 'default';
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="glass-panel p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl sm:text-3xl font-extrabold flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <div className="p-2.5 rounded-xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
              <HistoryIcon fontSize="large" />
            </div>
            <span>Audit & System Activity Logs</span>
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Track user activity, system changes, user roles, timestamps, and security events in plain language.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="glass-btn-secondary px-4 py-2 text-xs flex items-center gap-2"
            onClick={() => { fetchMetadata(); fetchLogs(); }}
          >
            <RefreshIcon fontSize="small" /> Refresh
          </button>
          <button
            className="glass-btn-primary px-4 py-2 text-xs flex items-center gap-2"
            onClick={handleExportCSV}
          >
            <DownloadIcon fontSize="small" /> Export CSV
          </button>
          {userData?.role === 'SuperAdmin' && (
            <button
              className="glass-btn-danger px-4 py-2 text-xs flex items-center gap-2"
              onClick={handleClearLogs}
            >
              <DeleteSweepIcon fontSize="small" /> Clear Logs
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-5 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <AssessmentIcon fontSize="large" />
          </div>
          <div>
            <div className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total System Logs</div>
            <div className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.totalLogs}</div>
          </div>
        </div>

        <div className="glass-card p-5 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <HistoryIcon fontSize="large" />
          </div>
          <div>
            <div className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Actions Today</div>
            <div className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.actionsToday}</div>
          </div>
        </div>

        <div className="glass-card p-5 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
            <PersonIcon fontSize="large" />
          </div>
          <div>
            <div className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Active Admins Today</div>
            <div className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.activeUsersToday}</div>
          </div>
        </div>

        <div className="glass-card p-5 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <ViewModuleIcon fontSize="large" />
          </div>
          <div>
            <div className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Most Active Module</div>
            <div className={`text-xl font-bold truncate max-w-[140px] mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`} title={stats.topModule}>
              {stats.topModule}
            </div>
          </div>
        </div>
      </div>

      {/* Filters Toolbar */}
      <div className="glass-panel p-5 space-y-4">
        <div className={`flex items-center gap-2 font-bold text-sm ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
          <FilterAltIcon className="text-indigo-400" fontSize="small" /> Filter & Search Logs
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Search Keywords</label>
            <input
              type="text"
              placeholder="Action, email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="glass-input w-full"
            />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Module</label>
            <select
              value={selectedModule}
              onChange={(e) => { setSelectedModule(e.target.value); setPage(1); }}
              className="glass-input w-full"
            >
              <option value="">All Modules</option>
              {modulesList.map(mod => (
                <option key={mod} value={mod}>{mod}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>User Email / Role</label>
            <input
              type="text"
              placeholder="User email or role"
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
              className="glass-input w-full"
            />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Action Type</label>
            <input
              type="text"
              placeholder="e.g. LOGIN, CREATE"
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="glass-input w-full"
            />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="glass-input w-full"
            />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="glass-input w-full"
            />
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button 
            onClick={handleResetFilters}
            className={`text-xs font-semibold hover:underline ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="glass-panel overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-3">
            <CircularProgress color="primary" />
            <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading activity logs...</div>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-full glass-table">
              <thead>
                <tr>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Timestamp</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">User</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Action</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Module</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Activity Summary</th>
                  <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Status</th>
                  <th className="py-3.5 px-4 text-center font-semibold text-xs uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map((log) => {
                    const humanSummary = getHumanReadableSummary(log);
                    const humanAction = getHumanActionLabel(log.action);
                    return (
                      <tr key={log.id} className="transition-colors hover:bg-white/5">
                        <td className="py-3.5 px-4 whitespace-nowrap text-xs">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="py-3.5 px-4 whitespace-nowrap text-xs">
                          <div className={`font-semibold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                            {log.user_email || <span className="text-slate-400 font-normal">System</span>}
                          </div>
                          <div className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {log.user_role || 'System'}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 whitespace-nowrap text-xs">
                          <Chip
                            label={humanAction}
                            size="small"
                            color={getActionColor(log.action)}
                            variant="outlined"
                            style={{ fontWeight: 600, fontSize: '0.75rem' }}
                          />
                        </td>
                        <td className={`py-3.5 px-4 whitespace-nowrap text-xs font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                          {log.module}
                        </td>
                        <td className={`py-3.5 px-4 text-xs font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                          <span className="line-clamp-2 max-w-sm" title={humanSummary}>
                            {humanSummary}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 whitespace-nowrap text-xs">
                          <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                            log.status === 'SUCCESS' ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-500 border border-rose-500/30'
                          }`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 whitespace-nowrap text-center text-xs">
                          <Tooltip title="View Log Details & Payload">
                            <IconButton size="small" color="primary" onClick={() => handleOpenDetails(log)}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className={`py-8 text-center text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      No audit logs match your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Bar */}
        <div className={`px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 ${
          isDark ? 'border-white/10 bg-slate-950/40' : 'border-slate-200 bg-slate-50'
        }`}>
          <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Showing <span className="font-semibold">{logs.length > 0 ? (page - 1) * limit + 1 : 0}</span> to{' '}
            <span className="font-semibold">{Math.min(page * limit, totalLogs)}</span> of{' '}
            <span className="font-semibold">{totalLogs}</span> entries
          </div>

          <div className="flex items-center space-x-2">
            <label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Rows per page:</label>
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              className="glass-input text-xs py-1 px-2"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <Button
              size="small"
              disabled={page <= 1}
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
            >
              Previous
            </Button>
            <span className="text-xs px-1 font-medium">
              Page {page} of {totalPages}
            </span>
            <Button
              size="small"
              disabled={page >= totalPages}
              onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Log Details Modal */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle className={`border-b font-bold flex items-center justify-between ${
          isDark ? 'border-white/10 bg-slate-900 text-white' : 'border-slate-200 bg-slate-100 text-slate-900'
        }`}>
          <div className="flex items-center gap-2">
            <span>Log Activity #{selectedLog?.id}</span>
            <Chip 
              label={getHumanActionLabel(selectedLog?.action)} 
              color={selectedLog ? getActionColor(selectedLog.action) : 'default'} 
              size="small" 
            />
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
            selectedLog?.status === 'SUCCESS' ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-500 border border-rose-500/30'
          }`}>
            {selectedLog?.status}
          </span>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', px: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)} 
            aria-label="log details view tabs"
          >
            <Tab icon={<DescriptionIcon fontSize="small" />} iconPosition="start" label="Plain English Summary" />
            <Tab icon={<CodeIcon fontSize="small" />} iconPosition="start" label="Technical Payload (JSON)" />
          </Tabs>
        </Box>

        <DialogContent className="p-6 space-y-4">
          {selectedLog && (
            <div>
              {activeTab === 0 && (
                <div className="space-y-5 pt-2">
                  <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 flex items-start space-x-3">
                    {selectedLog.status === 'SUCCESS' ? (
                      <CheckCircleOutlineIcon className="text-indigo-500 mt-0.5" />
                    ) : (
                      <ErrorOutlineIcon className="text-rose-500 mt-0.5" />
                    )}
                    <div>
                      <h4 className="text-xs font-bold text-indigo-500 uppercase tracking-wide">Activity Description</h4>
                      <p className={`text-base font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {getHumanReadableSummary(selectedLog)}
                      </p>
                    </div>
                  </div>

                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm p-4 rounded-xl border ${
                    isDark ? 'bg-slate-900/60 border-white/10' : 'bg-slate-50 border-slate-200'
                  }`}>
                    <div>
                      <span className="font-semibold text-slate-400">Performed By:</span>{' '}
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedLog.user_email || 'System'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-400">Role:</span>{' '}
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedLog.user_role || 'System'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-400">Module / Section:</span>{' '}
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedLog.module}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-400">Timestamp:</span>{' '}
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{new Date(selectedLog.created_at).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-400">IP Address:</span>{' '}
                      <span className={`font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedLog.ip_address || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-400">Result Status:</span>{' '}
                      <span className={`font-semibold ${selectedLog.status === 'SUCCESS' ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Key Captured Details</h4>
                    {(() => {
                      const parsed = parseLogDetails(selectedLog.details);
                      if (!parsed || (typeof parsed === 'object' && Object.keys(parsed).length === 0)) {
                        return <div className="text-sm text-slate-400 italic p-3 rounded bg-slate-50 border">No detailed attributes recorded for this event.</div>;
                      }
                      if (typeof parsed !== 'object') {
                        return <div className="text-sm p-3 rounded bg-slate-50 border">{String(parsed)}</div>;
                      }

                      return (
                        <div className={`rounded-xl border divide-y ${
                          isDark ? 'bg-slate-900/80 border-white/10 divide-white/5' : 'bg-slate-50 border-slate-200 divide-slate-200'
                        }`}>
                          {Object.entries(parsed).map(([k, v]) => (
                            <div key={k} className="p-3 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                              <span className="font-semibold text-slate-400 font-mono capitalize">{k.replace(/_/g, ' ')}:</span>
                              <span className={`font-sans max-w-md truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {activeTab === 1 && (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold text-slate-400 uppercase">
                      Raw Event Payload (Developer JSON View)
                    </label>
                    <button 
                      className="glass-btn-secondary px-3 py-1 text-xs flex items-center gap-1.5"
                      onClick={handleCopyPayload}
                    >
                      <ContentCopyIcon fontSize="small" />
                      {copied ? 'Copied!' : 'Copy Payload'}
                    </button>
                  </div>
                  <div className="bg-slate-950 text-emerald-400 font-mono p-4 rounded-xl text-xs overflow-x-auto max-h-96 border border-white/10 shadow-inner">
                    {selectedLog.details ? (
                      (() => {
                        const parsed = parseLogDetails(selectedLog.details);
                        return <pre>{typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed)}</pre>;
                      })()
                    ) : (
                      <span className="text-slate-500 italic">No JSON payload recorded for this event.</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className={`border-t p-4 flex justify-between ${
          isDark ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-slate-100'
        }`}>
          <div className="text-xs text-slate-400">
            {activeTab === 0 ? "Switch tabs to view raw technical JSON" : "Switch tabs to view non-technical summary"}
          </div>
          <button onClick={handleCloseDetails} className="glass-btn-primary px-5 py-2 text-xs">
            Close
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
